import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    setServices(mockData.services);
  }, []);

  const serviceImages = [
    "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/2876iabq_Image%2025-08-2025%20at%2015.50.jpeg",
    "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/9i0jvyb4_Picture%206.jpg",
    "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/dq31eqb6_Image%2054.jpeg",
    "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/yplpgd3b_Image%2045.jpeg",
    "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/y7dhn2kw_Image%2047.jpeg",
    "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/jpi1iam1_Image%2049.jpeg"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with 3D Background */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 scale-110 opacity-40"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1598161966801-4c8898ba6305?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxwZWFjZWZ1bCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQzMDN8MA&ixlib=rb-4.1.0&q=85')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(6px)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/80 to-amber-900/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="backdrop-blur-sm bg-white/10 rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 drop-shadow-lg">
              Our Development Services
            </h1>
            <p className="text-xl text-white/90 max-w-4xl mx-auto mb-8 leading-relaxed drop-shadow-md">
              As owners and developers of memorial gardens, we specialize in creating exceptional 
              facilities from conception to completion, partnering with expert teams for ongoing operations.
            </p>
            <Badge variant="outline" className="text-amber-300 border-amber-300/50 bg-amber-900/20 px-6 py-3 text-lg backdrop-blur-sm">
              Development • Design • Partnership Management
            </Badge>
          </div>
        </div>
      </section>

      {/* Services Grid with Rich Imagery */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {services.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{category.category}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.items.map((service, index) => {
                  const imageIndex = (categoryIndex * 3 + index) % serviceImages.length;
                  const serviceImage = serviceImages[imageIndex];
                  
                  return (
                    <Card key={index} className="border-0 bg-white/95 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 overflow-hidden">
                      <div 
                        className="h-48 bg-cover bg-center relative"
                        style={{
                          backgroundImage: `url('${serviceImage}')`
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-xl font-bold text-white drop-shadow-lg">{service.title}</h3>
                        </div>
                      </div>
                      <CardContent className="p-8">
                        <p className="text-gray-700 mb-8 leading-relaxed">{service.description}</p>
                        <div className="space-y-4">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-xl shadow-sm">
                              <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mr-4"></div>
                              <span className="text-gray-800 font-medium">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section with 3D Elements */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1641652800027-f06a8db92648?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx3YXRlciUyMGZlYXR1cmVzfGVufDB8fHx8MTc1NjEyNDI5MXww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 to-emerald-900/85"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Our Development Process</h2>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              From initial concept to operational handover, we manage every aspect of 
              memorial garden development with expertise and care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Planning & Design",
                description: "Site assessment, planning permission, and landscape architecture design tailored to location and community needs.",
                gradient: "from-amber-500 to-orange-600"
              },
              {
                step: "2", 
                title: "Development",
                description: "Infrastructure development, landscaping implementation, and facility construction with sustainability at the forefront.",
                gradient: "from-emerald-500 to-teal-600"
              },
              {
                step: "3",
                title: "Quality Assurance", 
                description: "Ensuring compliance with all regulatory standards, professional accreditation, and quality benchmarks.",
                gradient: "from-blue-500 to-indigo-600"
              },
              {
                step: "4",
                title: "Operational Handover",
                description: "Partnership with experienced management teams for ongoing operations while maintaining our quality standards.",
                gradient: "from-purple-500 to-pink-600"
              }
            ].map((process, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 border border-white/20">
                  <div className={`w-20 h-20 bg-gradient-to-br ${process.gradient} rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-3xl font-bold text-white">{process.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{process.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards Section with Visual Elements */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-amber-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">Professional Standards</h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              All our developments meet or exceed the highest industry standards and regulatory requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "ICCM Standards",
                description: "Institute of Cemetery and Crematorium Management professional standards compliance.",
                image: "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/snyp6hvf_Image%2053.jpeg"
              },
              {
                title: "FBCA Inspections", 
                description: "Regular inspections by the Federation of Burial and Cremation Authorities.",
                image: "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/wl9q3xzt_Image%2052.jpeg"
              },
              {
                title: "Ministry Compliance",
                description: "Full compliance with Ministry of Justice and English Heritage requirements.",
                image: "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/awwiygd6_Image%2051.jpeg"
              },
              {
                title: "Green Flag Awards",
                description: "Meeting Green Flag award standards for exceptional green space management.",
                image: "https://customer-assets.emergentagent.com/job_fenix-company/artifacts/mc1tg0g1_Image%2050.jpeg"
              }
            ].map((standard, index) => (
              <Card key={index} className="overflow-hidden border-0 bg-white/90 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105">
                <div 
                  className="h-32 bg-cover bg-center relative"
                  style={{
                    backgroundImage: `url('${standard.image}')`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{standard.title}</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{standard.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1544862870-a4d5e74dc313?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQzMDN8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-amber-900/90"></div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="backdrop-blur-sm bg-white/10 rounded-3xl p-12 border border-white/20 shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Ready to Discuss Your Project?</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Whether you're exploring development opportunities or seeking partnership possibilities, 
              we'd love to discuss how we can create something beautiful together.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300">
                  Get In Touch
                </Button>
              </Link>
              <Link to="/masterplan">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-4 text-lg font-semibold backdrop-blur-sm shadow-2xl transform hover:scale-105 transition-all duration-300">
                  View The Masterplan
                  <ArrowRight className="ml-3" size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;